import type { BrandBrief, Customizations } from '../types/manifest.js';

type Listener<T> = (value: T) => void;

export class Signal<T> {
  private listeners = new Set<Listener<T>>();
  constructor(private value: T) {}
  get(): T { return this.value; }
  set(next: T): void {
    if (Object.is(this.value, next)) return;
    this.value = next;
    for (const l of this.listeners) l(next);
  }
  update(fn: (prev: T) => T): void { this.set(fn(this.value)); }
  subscribe(fn: Listener<T>): () => void {
    this.listeners.add(fn);
    fn(this.value);
    return () => this.listeners.delete(fn);
  }
}

export type WeatherMode = 'sunny' | 'rainy' | 'snowing';
export type TempBand = 'cold' | 'temperate' | 'hot';
export type AuthState = 'checking' | 'login' | 'authed';
export type InitState =
  | { phase: 'awaiting' }
  | { phase: 'loading'; key: string }
  | { phase: 'generating'; key: string; completed: number; total: number }
  | { phase: 'ready'; key: string };

export const auth = new Signal<AuthState>('checking');
export const brand = new Signal<BrandBrief | null>(null);
export const customizations = new Signal<Customizations>({ slots: {} });
export const init = new Signal<InitState>({ phase: 'awaiting' });
export const prospect = new Signal<string | null>(null);
export const temperatureF = new Signal<number>(70);
export const weather = new Signal<WeatherMode>('sunny');
export const tornadoActive = new Signal<boolean>(false);
export const triggeredOverrideMs = new Signal<number>(20_000);

export interface GlobalStatus { busy: boolean; message: string }
export const globalStatus = new Signal<GlobalStatus>({ busy: false, message: '' });

export function startStatus(message: string): () => void {
  globalStatus.set({ busy: true, message });
  return () => globalStatus.set({ busy: false, message: '' });
}

export function bandFor(f: number): TempBand {
  if (f < 40) return 'cold';
  if (f > 90) return 'hot';
  return 'temperate';
}
